/**
 * Add Project Modal - Form to create new EAS project
 */

import { useState } from "react"
import toast from "react-hot-toast"

import { Button, Input } from "@aix/ui"
import { trpc } from "~/lib/trpc"

interface AddProjectModalProps {
  onClose: () => void
  onSuccess: () => void
}

export function AddProjectModal({ onClose, onSuccess }: AddProjectModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    icon: "",
    easAppId: "",
    expoSlug: "",
    platforms: [] as ("ios" | "android")[],
    channels: ["production", "development"],
    companyId: "",
    accessToken: "", // Store token in database (encrypted by Supabase)
  })

  // Fetch companies for selection
  const { data: companiesData, isLoading: loadingCompanies } = trpc.companies.list.useQuery({
    status: "active",
  })

  const createProject = trpc.easUpdates.createProject.useMutation({
    onSuccess: () => {
      onSuccess()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  // Get selected company details
  const selectedCompany = companiesData?.companies.find((c) => c.id === formData.companyId)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!formData.name || !formData.slug || !formData.easAppId || !formData.expoSlug) {
      toast.error("Please fill in all required fields")
      return
    }

    if (!formData.companyId) {
      toast.error("Please select a company")
      return
    }

    if (!formData.accessToken) {
      toast.error("Please provide an EAS access token")
      return
    }

    if (formData.platforms.length === 0) {
      toast.error("Please select at least one platform")
      return
    }

    createProject.mutate({
      ...formData,
      channels: formData.channels.filter((c) => c.trim().length > 0),
    })
  }

  const togglePlatform = (platform: "ios" | "android") => {
    setFormData((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter((p) => p !== platform)
        : [...prev.platforms, platform],
    }))
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border border-border rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-foreground mb-4">Add New EAS Project</h2>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">Project Name *</label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Pebble"
              className="w-full"
            />
          </div>

          {/* Slug */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">Slug * (lowercase, no spaces)</label>
            <Input
              type="text"
              value={formData.slug}
              onChange={(e) =>
                setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })
              }
              placeholder="pebble"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground mt-1">Used in URLs: /eas-dashboard/{formData.slug || "slug"}</p>
          </div>

          {/* Icon */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">Icon (Emoji)</label>
            <Input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="📱"
              className="w-full"
              maxLength={2}
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="AI-powered productivity app for iOS"
              className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground"
              rows={3}
            />
          </div>

          {/* EAS App ID */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">EAS App ID *</label>
            <Input
              type="text"
              value={formData.easAppId}
              onChange={(e) => setFormData({ ...formData, easAppId: e.target.value })}
              placeholder="abc123-456def-789ghi"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground mt-1">Find this in your Expo dashboard</p>
          </div>

          {/* Expo Slug */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">Expo Slug *</label>
            <Input
              type="text"
              value={formData.expoSlug}
              onChange={(e) => setFormData({ ...formData, expoSlug: e.target.value })}
              placeholder="@aixlabs/pebble-expo"
              className="w-full"
            />
          </div>

          {/* Platforms */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">Platforms *</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.platforms.includes("ios")}
                  onChange={() => togglePlatform("ios")}
                  className="rounded"
                />
                <span className="text-sm">📱 iOS</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.platforms.includes("android")}
                  onChange={() => togglePlatform("android")}
                  className="rounded"
                />
                <span className="text-sm">🤖 Android</span>
              </label>
            </div>
          </div>

          {/* Channels */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">Channels (comma-separated)</label>
            <Input
              type="text"
              value={formData.channels.join(", ")}
              onChange={(e) => setFormData({ ...formData, channels: e.target.value.split(",").map((c) => c.trim()) })}
              placeholder="production, development, staging"
              className="w-full"
            />
          </div>

          {/* Company Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Company * (for organizational grouping)
            </label>
            {loadingCompanies ? (
              <div className="text-sm text-muted-foreground">Loading companies...</div>
            ) : companiesData?.companies && companiesData.companies.length > 0 ? (
              <>
                <select
                  value={formData.companyId}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                  className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground"
                >
                  <option value="">-- Select a company --</option>
                  {companiesData.companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                {selectedCompany && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Project will be associated with: <span className="font-semibold">{selectedCompany.name}</span>
                  </p>
                )}
              </>
            ) : (
              <div className="text-sm text-muted-foreground">
                No companies found. Please create a company first in the Companies page.
              </div>
            )}
          </div>

          {/* EAS Access Token */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              EAS Access Token * (stored encrypted in database)
            </label>
            <Input
              type="password"
              value={formData.accessToken}
              onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
              placeholder="Paste your EAS access token here"
              className="w-full font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Get your token from:{" "}
              <a
                href="https://expo.dev/accounts/[account]/settings/access-tokens"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Expo Dashboard → Access Tokens
              </a>
              <br />
              Token is encrypted by Supabase and stored securely in the database.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button type="button" onClick={onClose} className="bg-secondary">
              Cancel
            </Button>
            <Button type="submit" loading={createProject.isLoading}>
              Create Project
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
